import { useState, useMemo } from 'react'
import { Plus, Edit2, Trash2, CalendarCheck } from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { deleteDocument } from '../../services/firebase'
import { formatDate, groupBy, todayISO } from '../../utils/helpers'
import { Button, Modal, Input, Textarea, Select, Badge } from '../../components/common'

const AssignmentForm = ({ assignment, departments, workers, onSave, onClose }) => {
  const [formData, setFormData] = useState(assignment || {
    date: '',
    serviceName: 'Sunday Worship Service',
    serviceTime: '',
    departmentId: '',
    workerUid: '',
    duty: '',
    notes: '',
  })
  const [saving, setSaving] = useState(false)

  const deptOptions = [
    { value: '', label: 'Select department...' },
    ...departments.map((d) => ({ value: d.id, label: d.name })),
  ]

  // Prefer workers assigned to the department; fall back to any approved worker
  const eligibleWorkers = useMemo(() => {
    const approved = workers.filter(
      (w) => w.status === 'approved' && w.isActive !== false
    )
    if (!formData.departmentId) return approved
    const members = approved.filter((w) =>
      (w.departmentIds || []).includes(formData.departmentId)
    )
    return members.length > 0 ? members : approved
  }, [workers, formData.departmentId])

  const workerOptions = [
    { value: '', label: 'Select worker...' },
    ...eligibleWorkers.map((w) => ({ value: w.id, label: w.displayName || w.email })),
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.departmentId || !formData.workerUid) return
    setSaving(true)
    const dept = departments.find((d) => d.id === formData.departmentId)
    const worker = workers.find((w) => w.id === formData.workerUid)
    await onSave({
      ...formData,
      departmentName: dept?.name || formData.departmentId,
      workerName: worker?.displayName || worker?.email || '',
      status: assignment?.status || 'assigned',
    })
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        <Input
          label="Date"
          type="date"
          required
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
        />
        <Input
          label="Service / Event"
          required
          value={formData.serviceName}
          onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
        />
        <Input
          label="Time"
          type="time"
          value={formData.serviceTime}
          onChange={(e) => setFormData({ ...formData, serviceTime: e.target.value })}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Select
          label="Department"
          required
          options={deptOptions}
          value={formData.departmentId}
          onChange={(e) => setFormData({ ...formData, departmentId: e.target.value, workerUid: '' })}
        />
        <Select
          label="Worker"
          required
          options={workerOptions}
          value={formData.workerUid}
          onChange={(e) => setFormData({ ...formData, workerUid: e.target.value })}
        />
      </div>

      <Input
        label="Duty (optional)"
        value={formData.duty}
        onChange={(e) => setFormData({ ...formData, duty: e.target.value })}
        placeholder="Lead vocals, sound desk, greeting..."
      />

      <Textarea
        label="Notes (optional)"
        rows={2}
        value={formData.notes}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
      />

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {assignment ? 'Update Assignment' : 'Add Assignment'}
        </Button>
      </div>
    </form>
  )
}

const SchedulesManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [filter, setFilter] = useState('upcoming')

  const { data: assignments, add, update, remove } = useCollection('assignments', {
    orderByField: 'date',
    orderDirection: 'asc',
  })
  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })
  const { data: workers } = useCollection('users', {
    orderByField: null,
    whereClause: { field: 'role', operator: '==', value: 'worker' },
  })

  const today = todayISO()
  const filtered = useMemo(() => {
    const list = assignments || []
    if (filter === 'upcoming') return list.filter((a) => a.date >= today)
    if (filter === 'past') return list.filter((a) => a.date < today)
    return list
  }, [assignments, filter, today])

  const byDate = useMemo(() => groupBy(filtered, 'date'), [filtered])
  const dates = Object.keys(byDate).sort((a, b) =>
    filter === 'past' ? b.localeCompare(a) : a.localeCompare(b)
  )

  const handleSave = async (data) => {
    if (editing?.id) {
      await update(editing.id, data)
      // Re-arm the reminder if the slot moved — the scheduler skips anything
      // with an existing remindersSent doc.
      if (data.date !== editing.date || data.serviceTime !== editing.serviceTime) {
        try {
          await deleteDocument('remindersSent', editing.id)
        } catch (err) {
          console.warn('Could not reset reminder for assignment:', err)
        }
      }
    } else {
      await add(data)
    }
    setShowModal(false)
    setEditing(null)
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    await remove(deleteConfirm)
    try {
      await deleteDocument('remindersSent', deleteConfirm)
    } catch (err) {
      console.warn('Could not remove reminder record:', err)
    }
    setDeleteConfirm(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Schedules</h1>
          <p className="text-gray-500">
            Assign workers to services — they get email/SMS reminders automatically
          </p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditing(null)
            setShowModal(true)
          }}
        >
          Add Assignment
        </Button>
      </div>

      <div className="flex gap-2">
        {['upcoming', 'past', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
              filter === f
                ? 'bg-primary-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {dates.map((date) => (
          <div key={date} className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <p className="font-heading font-semibold text-dark-100">
                {formatDate(date, 'EEEE, MMMM d, yyyy')}
              </p>
              <Badge variant="gray" size="sm">
                {byDate[date].length} assignment{byDate[date].length === 1 ? '' : 's'}
              </Badge>
            </div>
            <table className="w-full">
              <tbody className="divide-y divide-gray-100">
                {byDate[date].map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3">
                      <p className="font-medium text-dark-100">{a.workerName}</p>
                      <p className="text-sm text-gray-500">{a.duty || '—'}</p>
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant="primary" size="sm">{a.departmentName}</Badge>
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      <p>{a.serviceName}</p>
                      {a.serviceTime && <p className="text-sm text-gray-400">{a.serviceTime}</p>}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setEditing(a)
                            setShowModal(true)
                          }}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(a.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {dates.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg text-center py-12 text-gray-500">
            <CalendarCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No {filter} assignments</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditing(null)
        }}
        title={editing ? 'Edit Assignment' : 'Add Assignment'}
        size="lg"
      >
        <AssignmentForm
          assignment={editing}
          departments={departments || []}
          workers={workers || []}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditing(null)
          }}
        />
      </Modal>

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Assignment"
        size="sm"
      >
        <p className="text-gray-600 mb-6">Remove this assignment from the schedule?</p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default SchedulesManager
